import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, ShoppingBag, Eye, Heart, ArrowLeft } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import Card from '../common/Card';
import Button from '../common/Button';
import Toast from '../common/Toast';
import productService from '../../services/productService';

const SearchPage = () => {
    const location = useLocation();
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const query = params.get('q') || '';
        setSearchTerm(query);

        if (query.trim() === '') {
            setFilteredProducts([]);
        } else {
            const fetchResults = async () => {
                try {
                    setLoading(true);
                    const results = await productService.searchProducts(query);
                    setFilteredProducts(results);
                    setLoading(false);
                } catch (err) {
                    console.error('Search error:', err);
                    setError('Une erreur est survenue lors de la recherche.');
                    setLoading(false);
                }
            };
            fetchResults();
        }
    }, [location.search]);

    const handleAddToCart = (product) => {
        const wasAdded = addToCart(product);
        if (wasAdded) {
            setToastMessage("Produit ajouté au panier !");
        } else {
            setToastMessage("Ce produit est déjà dans votre panier.");
        }
        setShowToast(true);
    };

    return (
        <div className="bg-white pt-32 pb-24 min-h-screen">
            <div className="container mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                    <div>
                        <Link to="/" className="inline-flex items-center gap-2 text-brand font-semibold mb-4 hover:gap-3 transition-all">
                            <ArrowLeft className="w-5 h-5" />
                            Retour à l'accueil
                        </Link>
                        <h1 className="text-4xl font-extrabold text-brand-dark">
                            {searchTerm ? `Résultats pour "${searchTerm}"` : "Rechercher un produit"}
                        </h1>
                        <p className="text-slate-500 mt-2">
                            {filteredProducts.length} {filteredProducts.length > 1 ? 'produits trouvés' : 'produit trouvé'}
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 space-y-6">
                        <div className="w-16 h-16 border-4 border-brand/20 border-t-brand rounded-full animate-spin"></div>
                        <p className="text-brand-dark/60 font-medium">Recherche en cours...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-20 bg-white rounded-[3rem] border border-slate-100 shadow-xl max-w-2xl mx-auto px-10">
                        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-8">
                            <Eye className="w-10 h-10 opacity-50" />
                        </div>
                        <h3 className="text-2xl font-bold text-brand-dark mb-4">Erreur</h3>
                        <p className="text-slate-500 mb-8">{error}</p>
                        <Button variant="primary" onClick={() => window.location.reload()}>Réessayer</Button>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="w-20 h-20 bg-brand-dark/5 rounded-full flex items-center justify-center text-brand/50 mb-6">
                            <Search className="w-10 h-10" />
                        </div>
                        <h2 className="text-2xl font-bold text-brand-dark mb-4">Aucun résultat trouvé</h2>
                        <p className="text-slate-500 max-w-md">
                            Désolé, nous n'avons trouvé aucun produit correspondant à votre recherche{searchTerm ? ` pour "${searchTerm}"` : ""}. Essayez d'autres mots-clés ou parcourez nos catégories.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                        {filteredProducts.map((product) => (
                            <Card key={product.id} className="group h-full flex flex-col">
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />

                                    {/* Stock Badge */}
                                    {product.stock === 0 && (
                                        <div className="absolute top-4 left-4 z-10">
                                            <span className="px-3 py-1.5 bg-red-500 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg">
                                                Épuisé
                                            </span>
                                        </div>
                                    )}

                                    {/* Quick Actions Overlay */}
                                    <div className="absolute inset-0 bg-brand-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                                        <Link to={`/product/${product.id}`} className="w-12 h-12 bg-white text-brand-dark rounded-full flex items-center justify-center hover:bg-brand-dark hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 shadow-xl">
                                            <Eye className="w-5 h-5" />
                                        </Link>
                                        <button
                                            onClick={() => toggleFavorite(product)}
                                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-[100ms] shadow-xl ${isFavorite(product.id)
                                                ? 'bg-brand text-white'
                                                : 'bg-white text-brand-dark hover:bg-brand-dark hover:text-white'
                                                }`}
                                        >
                                            <Heart className={`w-5 h-5 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                                        </button>
                                        <button
                                            onClick={() => product.stock > 0 && handleAddToCart(product)}
                                            disabled={product.stock === 0}
                                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-[200ms] shadow-xl ${product.stock === 0 ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white text-brand-dark hover:bg-brand-dark hover:text-white'}`}
                                        >
                                            <ShoppingBag className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="p-8 flex flex-col flex-grow text-left">
                                    <div className="mb-6">
                                        <Link to={`/product/${product.id}`}>
                                            <h4 className="text-xl sm:text-2xl font-extrabold text-brand-dark group-hover:text-brand transition-colors leading-tight mb-3">
                                                {product.name}
                                            </h4>
                                        </Link>
                                        <div className="flex items-center justify-between mb-4">
                                            <p className="text-xl font-black text-brand">
                                                {Math.floor(product.price).toLocaleString('fr-FR')} <span className="text-xs font-bold text-slate-400 uppercase">fcfa</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-auto">
                                        <button
                                            onClick={() => product.stock > 0 && handleAddToCart(product)}
                                            disabled={product.stock === 0}
                                            className={`w-full h-14 border-2 rounded-2xl text-sm font-bold transition-all duration-500 flex items-center justify-center gap-3 group/btn ${product.stock === 0
                                                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                : 'bg-brand/5 text-brand-dark border-brand/10 hover:bg-brand-dark hover:text-white hover:border-brand-dark'
                                                }`}
                                        >
                                            <ShoppingBag className="w-5 h-5 transition-transform group-hover/btn:scale-110" />
                                            {product.stock === 0 ? 'Rupture de Stock' : 'Ajouter au Panier'}
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
            <Toast
                message={toastMessage}
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </div>
    );
};

export default SearchPage;

